import { TranslationsContext } from '@teliaee/telia-translate';
import { media, Heading, Notice, Separator, Spinner, Tabs, TabsItem } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import React from 'react';
import MediaQuery from 'react-responsive';
import RoamingStore from '../../stores/RoamingStore';
import DangerNotices from './DangerNotices';
import EmergencyNumbers from './EmergencyNumbers';
import SpecialNumbers from './SpecialNumbers';

const TAB_SPECIAL_NUMBERS = 'special-numbers';
const TAB_EMERGENCY = 'emergency';
const TAB_DANGER_NOTICES = 'danger-notices';

interface RoamingSpecialNumbersProps {
  roamingStore?: RoamingStore;
}

interface RoamingSpecialNumbersState {
  currentTab: string;
  showAllSpecialNumbers: boolean;
}

@observer
class RoamingSpecialAndEmergencyTable extends React.Component<RoamingSpecialNumbersProps, RoamingSpecialNumbersState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private tabs = [TAB_SPECIAL_NUMBERS, TAB_EMERGENCY, TAB_DANGER_NOTICES];

  constructor(props: RoamingSpecialNumbersProps) {
    super(props);
    this.state = { currentTab: TAB_SPECIAL_NUMBERS, showAllSpecialNumbers: false };
  }

  private getTabResultsTotal(tabId: string): number {
    const {
      roamingStore: { roamingBerecInformation }
    } = this.props;

    switch (tabId) {
      case TAB_SPECIAL_NUMBERS:
        return roamingBerecInformation.berecInformation.vasNumbers.length;
      case TAB_EMERGENCY:
        return roamingBerecInformation.berecInformation.emergencyNumbers.length;
      case TAB_DANGER_NOTICES:
        return roamingBerecInformation.berecInformation.pwsNumbers.length;
      default:
        return undefined;
    }
  }

  private handleTabChange(tabId: string): void {
    this.setState({ currentTab: tabId });
  }

  private static getTabContent(tabId: string): JSX.Element {
    switch (tabId) {
      case TAB_SPECIAL_NUMBERS:
        return <SpecialNumbers />;
      case TAB_EMERGENCY:
        return <EmergencyNumbers />;
      case TAB_DANGER_NOTICES:
        return <DangerNotices />;
      default:
        return null;
    }
  }

  private renderBerecInfo(isLargeScreen: boolean): JSX.Element {
    const translate = this.context.translate;
    const { currentTab } = this.state;

    return (
      <Tabs
        aria-labelledby="special-numbers-info"
        border="top"
        currentTab={currentTab}
        defaultCurrentTab={currentTab}
        onTabChange={(prevTab, nextTab) => this.handleTabChange(nextTab)}
        navigationType={isLargeScreen ? undefined : 'button-navigation'}
        size={isLargeScreen ? undefined : 'small'}
        headingLevel={4}
      >
        {this.tabs.map((tab) => (
          <TabsItem key={tab} id={tab} label={`${translate(`roaming.${tab}.title`)} (${this.getTabResultsTotal(tab)})`}>
            {RoamingSpecialAndEmergencyTable.getTabContent(tab)}
          </TabsItem>
        ))}
      </Tabs>
    );
  }

  public render() {
    const translate = this.context.translate;
    const {
      roamingStore: { selectedCountry, roamingBerecInformation }
    } = this.props;

    if (!selectedCountry || !selectedCountry.eea || !roamingBerecInformation.berecInformation) {
      return null;
    }

    return (
      <>
        <Heading level={3} className="h2" id="special-numbers-info">
          {translate('roaming.special-and-emergency-numbers.title')}
        </Heading>
        <Separator spacing="small" color="invisible" />
        {!roamingBerecInformation || roamingBerecInformation.status !== 'FETCHED' ? (
          <Spinner />
        ) : (
          <MediaQuery minWidth={media.width.md.min}>{this.renderBerecInfo.bind(this)}</MediaQuery>
        )}
        <Separator color="invisible" spacing="small" />
        <Notice type="info">{translate('roaming.berec.legal-notice')}</Notice>
      </>
    );
  }
}

export default inject('roamingStore')(RoamingSpecialAndEmergencyTable);
